// =====================================================================
// Conservação Predial — a leitura de FILA dos programas.
//
// Irmã de `certificacao/certificacao-fila-utils.js`, mas NÃO é cópia: lá há
// dois pares de assinatura (diagnóstico + certificação) e aqui há um só —
// com um gate que a Certificação não tem, as Ordens de Serviço. Certificar
// com OS em aberto seria atestar intervenção que não foi feita, então o
// servidor recusa (409); a coluna precisa refletir isso, senão manda o
// gerente a um botão que não funciona.
//
// Responde as duas perguntas que o STATUS sozinho não responde:
//   1. "Quem está segurando este programa?"  → aguardandoDe()
//   2. "Há quanto tempo ele está parado?"    → diasParado()
//
// Puras de propósito (entram os campos do programa, sai texto/número): é a
// única lógica de risco da tela — tabela e filtro não se testam.
// =====================================================================

const GERENTE = 'Gerente';
const CONSULTOR = 'Consultor';

// Quem ainda não assinou (coluna de nome preenchida = assinou), na ordem em
// que os papéis aparecem no programa.
const faltantes = (nomeGerente, nomeConsultor) => {
  const f = [];
  if (!nomeGerente) f.push(GERENTE);
  if (!nomeConsultor) f.push(CONSULTOR);
  return f;
};

/**
 * Quem o programa está esperando, em linguagem de quem opera o programa.
 *
 * Devolve `{ texto, papeis, tipo }`:
 *   - `texto`  → o que aparece na coluna ("Validação: Gerente")
 *   - `papeis` → ['Gerente'] — para o gerente reconhecer a própria fila
 *   - `tipo`   → 'validacao' | 'trabalho' | 'nenhum' (governa a cor do chip)
 *
 * ⚠️ A ORDEM IMPORTA: a OS vem ANTES da assinatura. Um programa com Ordem de
 * Serviço em aberto está com quem executa o serviço, não com quem valida —
 * e o backend barra a validação enquanto ela existir (gate da Fase 3).
 */
export const aguardandoDe = (p) => {
  const nada = { texto: '—', papeis: [], tipo: 'nenhum' };
  if (!p) return nada;

  switch (p.STATUS) {
    case 'Certificado':
    case 'Cancelado':
      // Fim de linha: ninguém deve nada.
      return nada;

    case 'Rascunho':
      // A inspeção nem foi finalizada — está com quem preenche.
      return { texto: 'Preenchimento (rascunho)', papeis: [], tipo: 'trabalho' };

    case 'Inspecionado':
    case 'Em execução': {
      const abertas = Number(p.QTD_OS_ABERTAS) || 0;
      if (abertas > 0) {
        return {
          texto: `Execução: ${abertas} OS em aberto`,
          papeis: [], tipo: 'trabalho',
        };
      }
      const f = faltantes(p.VALID_GERENTE_NOME, p.VALID_CONSULTOR_NOME);
      // Sem OS e com os dois assinados o backend já teria promovido a
      // 'Certificado' — se acontecer, é dado torto: não invente cobrança.
      if (f.length === 0) return nada;
      return { texto: `Validação: ${f.join(' + ')}`, papeis: f, tipo: 'validacao' };
    }

    default:
      return nada;
  }
};

/**
 * Data do último ato de negócio do programa — o marco a partir do qual ele
 * está parado.
 *
 * ⚠️⚠️ NÃO usar DATA_MODIFICACAO: é `on update CURRENT_TIMESTAMP` e carimba
 * qualquer UPDATE, inclusive administrativo (na Certificação ela rejuvenescia
 * 11 dos 24 relatórios, todos com o carimbo de uma migração).
 *
 * ⚠️ A lista tem um ato a mais que a da Certificação: a EXECUÇÃO DA OS. Entre
 * a inspeção e a assinatura pode haver meses de serviço rodando — sem ela, um
 * programa cuja OS foi trabalhada ontem apareceria parado desde a inspeção.
 */
export const marcoUltimoAto = (p) => {
  if (!p) return null;
  const datas = [
    p.VALID_GERENTE_DATA, p.VALID_CONSULTOR_DATA,
    p.ULTIMA_OS_EXECUCAO,
    p.DATA_INSPECAO,
  ].filter(Boolean).map((d) => new Date(d)).filter((d) => !isNaN(d));

  if (datas.length) return new Date(Math.max(...datas));
  const criacao = p.DATA_CRIACAO ? new Date(p.DATA_CRIACAO) : null;
  return criacao && !isNaN(criacao) ? criacao : null;
};

/**
 * Dias corridos desde o último ato de negócio. `null` quando não há marco
 * algum (a coluna mostra "—" em vez de inventar zero).
 *
 * Dias CORRIDOS, não úteis: mede-se o tempo que o prédio passou sem
 * tratamento, e ele não faz pausa no fim de semana.
 */
export const diasParado = (p, hoje = new Date()) => {
  const marco = marcoUltimoAto(p);
  if (!marco) return null;
  // Zera a hora dos dois lados: interessa a distância em dias de calendário,
  // não o acaso de a inspeção ter sido assinada de manhã ou à noite.
  const dia = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(0, Math.round((dia(hoje) - dia(marco)) / 86400000));
};

/** Programa ainda em andamento (o oposto de arquivo). */
export const emAndamento = (p) => !['Certificado', 'Cancelado'].includes(p?.STATUS);

/**
 * Ordena a fila pelo mais parado no topo. Sem marco vai para o fim (não dá
 * para dizer que está parado há muito o que não se sabe quando começou).
 */
export const ordenarPorParado = (lista) => lista.slice().sort((a, b) => {
  const da = diasParado(a), db = diasParado(b);
  if (da === null && db === null) return 0;
  if (da === null) return 1;
  if (db === null) return -1;
  return db - da || b.ID - a.ID;
});
